import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';

import {
  DRAFT_TEXT, ERROR, MUTED, WARNING_HINT,
} from './common/tokens';
import {
  DISABLED_BG, TagEditButton, TagEditIcon, TagEditShell, tagEditHintSx, tagEditInputSx,
} from './common/TagEditChip';

// A tag that has been asked for but hasn't arrived: alive, but not solid yet.
const shimmer = keyframes`
  0%   { opacity: 0.55; }
  50%  { opacity: 0.9; }
  100% { opacity: 0.55; }
`;

// Tag names are lowercase and single-spaced; a leading '#' is dropped if present.
export function normalizeTagInput(raw) {
  return raw.toLowerCase().replace(/^#+/, '').replace(/\s+/g, ' ');
}

/**
 * A tag whose create is still in flight, or that failed. Sits in the grid in
 * the place the real chip will take, so the row doesn't reshuffle on arrival.
 */
export function TagDraftChip({ draft, onRetry }) {
  const theme = useTheme();
  const successGreen = theme.palette.success.main;
  const failed = draft.status === 'error';

  return (
    <Box sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      border: failed ? `1px solid ${ERROR}` : `1px dashed ${successGreen}`,
      borderRadius: '10px',
      padding: '2px 8px',
      fontSize: '0.85rem',
      letterSpacing: '0.5px',
      textTransform: 'lowercase',
      color: failed ? ERROR : DRAFT_TEXT,
      background: failed ? alpha(ERROR, 0.05) : '#fff',
      opacity: failed ? 1 : 0.8,
      animation: failed ? undefined : `${shimmer} 1s ease-in-out infinite`,
      userSelect: 'none',
    }}>
      {draft.name}
      {failed && (
        <Box
          component="button"
          type="button"
          onClick={() => onRetry(draft.key)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            border: 'none',
            background: 'transparent',
            color: ERROR,
            fontSize: 11,
            fontWeight: 700,
            fontFamily: 'inherit',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="1 4 1 10 7 10" />
            <path d="M3.51 15a9 9 0 1 0 .49-4.99" />
          </svg>
          Retry
        </Box>
      )}
    </Box>
  );
}

/**
 * Inline composer for a new tag. Shaped like a chip and placed at the head of
 * the grid.
 *
 * Enter adds and leaves the field open, so several tags can go in one after
 * another. A name that already exists never submits: it warns and asks the
 * caller to point at the chip that owns it.
 */
function TagComposer({
  existingNames, onCreate, onDuplicate, onClose,
}) {
  const theme = useTheme();
  const successGreen = theme.palette.success.main;
  const [value, setValue] = useState('');
  const [warnName, setWarnName] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  // The Esc handler can be swallowed before it fires (a browser extension may
  // take the first Esc to blur the field), the window keeps a fallback that
  // closes the composer either way.
  useEffect(() => {
    const handleWindowKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleWindowKeyDown);
    return () => window.removeEventListener('keydown', handleWindowKeyDown);
  }, [onClose]);

  const trimmed = normalizeTagInput(value).trim();
  const isDirty = trimmed.length > 0;

  const submit = useCallback(() => {
    if (!trimmed) return;
    if (existingNames.has(trimmed)) {
      setWarnName(trimmed);
      onDuplicate(trimmed);
      return;
    }
    setWarnName(null);
    setValue('');
    onCreate(trimmed);
    if (inputRef.current) inputRef.current.focus();
  }, [trimmed, existingNames, onCreate, onDuplicate]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const handleChange = (e) => {
    setValue(normalizeTagInput(e.target.value));
    setWarnName(null);
  };

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <TagEditShell>
        <TagEditIcon />
        <Box
          component="input"
          ref={inputRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="new tag"
          sx={tagEditInputSx(value.length, 96)}
        />
        <TagEditButton
          type="button"
          aria-label="Add tag"
          onMouseDown={(e) => { e.preventDefault(); submit(); }}
          sx={{
            background: isDirty ? successGreen : DISABLED_BG,
            color: '#fff',
            padding: '0 8px',
            cursor: isDirty ? 'pointer' : 'default',
            borderLeft: '1px solid rgba(0,0,0,0.08)',
            transition: 'background 0.15s',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </TagEditButton>
        <TagEditButton
          type="button"
          aria-label="Close"
          onMouseDown={(e) => { e.preventDefault(); onClose(); }}
          sx={{ background: 'transparent', color: MUTED, padding: '0 7px' }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </TagEditButton>
      </TagEditShell>

      {warnName ? (
        <Box sx={{ ...tagEditHintSx, color: WARNING_HINT }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {`“${warnName}” already exists — highlighted in the grid. Edit the name or press Esc.`}
        </Box>
      ) : (
        <Box sx={tagEditHintSx}>
          Enter to add · Esc to close
        </Box>
      )}
    </Box>
  );
}

export default TagComposer;
